import {FC} from 'react'
import {Settings} from 'react-slick'

import Carousel from '@/components/common/carousel/Carousel'
import {TeamMemberType} from '@/content/team'
import {Link} from '@/i18n'
import {SLIDER_TRANSITION} from '@/theme/cssTransitions'
import {theme} from '@/theme/theme'
import {routeTo} from '@/utils/router'
import {
  MOBILE_MAX_WIDTH,
  MOBILE_SCREENSIZE,
  TABLET_MAX_WIDTH,
  TABLET_SCREENSIZE,
} from '@/utils/styles/breakpoints'

import {Text, TextVariant} from '../content/Text'
import {TeamMember} from './TeamMember'

const settings: Settings = {
  infinite: true,
  speed: SLIDER_TRANSITION,
  slidesToShow: 4,
  slidesToScroll: 1,
  autoplay: true,
  autoplaySpeed: 6000,
  arrows: true,
  responsive: [
    {
      breakpoint: TABLET_MAX_WIDTH,
      settings: {
        slidesToShow: 3,
      },
    },
    {
      breakpoint: MOBILE_MAX_WIDTH,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 2,
        arrows: false,
        dots: true,
      },
    },
  ],
}

type TeamCarouselProps = {
  teamMembers: TeamMemberType[]
  numOfVisibleTeamMembers?: number
}

export const TeamCarousel: FC<TeamCarouselProps> = ({teamMembers, numOfVisibleTeamMembers}) => {
  return (
    <>
      <div className="team-carousel">
        <Carousel settings={settings}>
          {teamMembers.map((teamMember) => (
            <div key={teamMember.name} className="item">
              <TeamMember teamMember={teamMember} />
            </div>
          ))}
          {numOfVisibleTeamMembers && (
            <Link href={routeTo.aboutUs.home()} legacyBehavior>
              <a className="item show-more">
                <Text variant={TextVariant.h2} element="p" color={theme.palette.text.white}>
                  +{teamMembers.length - numOfVisibleTeamMembers}
                </Text>
              </a>
            </Link>
          )}
        </Carousel>
      </div>

      <style jsx>{`
        .team-carousel {
          margin-top: ${theme.spacing[6]};
        }
        .item {
          padding: 0 ${theme.spacing[3]};
        }
        .show-more {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 180px;
          border-radius: 16px;
          background-color: ${theme.palette.primary.main};
        }
        @media ${TABLET_SCREENSIZE} {
          .team-carousel {
            margin-top: ${theme.spacing[5]};
            margin-left: -${theme.spacing[2]};
            margin-right: -${theme.spacing[2]};
          }
          .item {
            padding: 0 ${theme.spacing[2]};
          }
          .show-more {
            margin-right: ${theme.spacing[2]};
          }
        }
      `}</style>

      {/* Please, use global styles only if you need to override CSS from external libraries */}
      <style jsx global>{`
        .team-carousel .slick-slider {
          max-width: 980px;
          margin: 0 auto;
        }
        @media ${TABLET_SCREENSIZE} {
          .team-carousel .slick-list {
            margin: 0 ${theme.spacing[4]};
          }
          .team-carousel .slick-prev {
            left: -4px;
          }
          .team-carousel .slick-next {
            right: -4px;
          }
        }
        @media ${MOBILE_SCREENSIZE} {
          .team-carousel .slick-slider {
            max-width: 400px;
          }
          .team-carousel .slick-list {
            margin: 0;
          }
          .team-carousel .slick-dots {
            margin-top: ${theme.spacing[3]};
          }
        }
      `}</style>
    </>
  )
}
